import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CalendarPlus, ChevronDown, Clock, Loader2, MessageSquare } from 'lucide-react';
import { apiGet } from '../../lib/api.js';
import { fmtDate } from '../../lib/format.js';
import { useAuth } from '../../store/auth.js';
import {
  SMS_MAX_CHARS, estimatedCost, insertAtCursor, parseSchedule, resolveKeywords, resolveMergeTags, smsSegments, withCardTag,
} from '../../lib/smsCompose.js';
import { SchedulePicker } from '../sms/SmsComposeTools.jsx';
import KeywordList from './KeywordList.jsx';
import EmojiButton from './EmojiButton.jsx';

const FIELD = 'w-full bg-civix-panelLight border border-civix-border rounded-lg px-3 py-3 text-sm text-white placeholder:text-white/40 focus:outline-hidden focus:border-civix-purple transition';

/**
 * TextTab — SMS compose / schedule panel tab. The typed number is resolved to a
 * lead (tenant + downline scoped) because texts are sent to leads. Keywords
 * (#first_name, #my_vcard_link, …) and the optional Digital Business Card link
 * are resolved server-side on send; the counter shows the resolved length.
 *
 * @param {Object} props
 * @param {string} props.textTo
 * @param {(v: string) => void} props.setTextTo
 * @param {string} props.textMsg
 * @param {(v: string) => void} props.setTextMsg
 * @param {boolean} props.textSending
 * @param {(p: {leadId: string, body: string, scheduledFor?: string}) => Promise<boolean>} props.handleSendText
 * @param {{leadId?: string, toNumber?: string}|null} [props.activeLead] - Lead on the current call, offered as the recipient.
 */
export default function TextTab({
  textTo,
  setTextTo,
  textMsg,
  setTextMsg,
  textSending,
  handleSendText,
  activeLead = null,
}) {
  const agent = useAuth((s) => s.agent);
  const textareaRef = useRef(null);
  const [schedule, setSchedule] = useState({ on: false, date: '', time: '' });
  const [sendCard, setSendCard] = useState(false);
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(textTo.replace(/[^\d+]/g, '')), 400);
    return () => clearTimeout(t);
  }, [textTo]);

  const { data: lookup, isFetching } = useQuery({
    queryKey: ['phone-lead-lookup', debounced],
    queryFn: () => apiGet(`/phone/lead-lookup?phone=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 10,
    staleTime: 30_000,
  });
  const { data: repliesData } = useQuery({
    queryKey: ['preset-replies'],
    queryFn: () => apiGet('/marketing/preset-replies'),
    staleTime: 60_000,
    refetchOnMount: 'always', // a reply saved in Marketing Hub shows up as soon as the panel opens
  });
  const presetReplies = repliesData?.presetReplies || [];

  const lead = debounced.length >= 10 ? lookup?.data?.lead || null : null;
  // `outgoing` is what we post (server resolves the keywords); `shown` is the same text resolved here for the counter/preview.
  const outgoing = withCardTag(resolveMergeTags(textMsg, lead, agent), sendCard);
  const shown = resolveKeywords(outgoing, lead, agent);
  const { segments } = smsSegments(shown);
  const hasKeywords = /[#{]/.test(textMsg) || sendCard;

  const missing = !textTo.trim() ? 'Phone number required'
    : !textMsg.trim() ? 'Message required'
    : !lead && !isFetching && debounced.length >= 10 ? 'No lead found for this number. Add them as a lead first.'
    : '';

  function insert(tag) {
    const { value, caret } = insertAtCursor(textMsg, textareaRef.current, tag);
    setTextMsg(value.slice(0, SMS_MAX_CHARS));
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      el?.focus();
      el?.setSelectionRange?.(caret, caret);
    });
  }

  async function submit() {
    if (!lead?._id || !textMsg.trim()) return;
    let scheduledFor;
    if (schedule.on) {
      const { at, error } = parseSchedule(schedule.date, schedule.time);
      if (error) { toast.error(error); return; }
      scheduledFor = at.toISOString();
    }
    const ok = await handleSendText({ leadId: lead._id, body: outgoing, ...(scheduledFor ? { scheduledFor } : {}) });
    if (!ok) return;
    toast.success(scheduledFor ? `Text scheduled for ${fmtDate(scheduledFor)}` : 'Text sent');
    setTextMsg('');
    setSendCard(false);
    setSchedule({ on: false, date: '', time: '' });
  }

  return (
    <div className="p-4 space-y-3" data-testid="text-tab">
      <input type="tel" value={textTo} onChange={(e) => setTextTo(e.target.value)} placeholder="Phone number" aria-label="Phone number" className={FIELD} />
      {activeLead?.toNumber && textTo !== activeLead.toNumber && (
        <button type="button" onClick={() => setTextTo(activeLead.toNumber)} className="text-[11px] text-civix-purpleSoft hover:text-white">
          Use current call number {activeLead.leadName ? `(${activeLead.leadName})` : ''}
        </button>
      )}
      {lead && (
        <div className="text-[11px] text-emerald-300">
          To: {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.phone}
        </div>
      )}

      {/* Caller ID — numbers are picked by the server's smart-local routing; it is the only mode the send API supports. */}
      <label className="relative block">
        <span className="absolute -top-2 left-3 px-1 bg-civix-panel text-[11px] text-white/50">Caller ID</span>
        <select aria-label="Caller ID" defaultValue="smart" className={`${FIELD} appearance-none pr-9`}>
          <option value="smart">Smart local ID</option>
        </select>
        <ChevronDown className="w-4 h-4 text-white/50 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      </label>

      <label className="relative block">
        <select
          aria-label="Send a preset reply"
          value=""
          onChange={(e) => {
            const r = presetReplies.find((p) => String(p._id) === e.target.value);
            if (r) setTextMsg(String(r.body || '').slice(0, SMS_MAX_CHARS));
          }}
          className={`${FIELD} appearance-none pr-9 ${presetReplies.length ? 'text-white' : 'text-white/40'}`}
        >
          <option value="">{presetReplies.length ? 'Send a preset reply' : 'No preset replies yet'}</option>
          {presetReplies.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
        </select>
        <ChevronDown className="w-4 h-4 text-white/50 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      </label>

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={textMsg}
          onChange={(e) => setTextMsg(e.target.value.slice(0, SMS_MAX_CHARS))}
          placeholder="Message"
          aria-label="Message"
          rows={5}
          maxLength={SMS_MAX_CHARS}
          className={`${FIELD} resize-none pr-12`}
        />
        <EmojiButton onPick={insert} />
      </div>

      <div className="flex items-center justify-between text-[11px] text-white/60" data-testid="sms-counter">
        <span>{shown.length}/{SMS_MAX_CHARS} ({segments} text message{segments === 1 ? '' : 's'})</span>
        <span>Estimated cost: {estimatedCost(segments)}</span>
      </div>

      <KeywordList onInsert={insert} />

      <label className="flex items-center gap-2.5 text-sm text-white/80 cursor-pointer w-fit">
        <input
          type="checkbox"
          className="w-4 h-4 accent-civix-purple"
          checked={sendCard}
          onChange={(e) => setSendCard(e.target.checked)}
          data-testid="send-card"
        />
        Send Digital Business Card?
      </label>

      {hasKeywords && lead && (
        <p className="text-[11px] text-white/50 rounded-lg bg-civix-panelLight border border-civix-border px-2 py-1 whitespace-pre-wrap">Preview: {shown}</p>
      )}

      <SchedulePicker value={schedule} onChange={setSchedule} />

      <button
        onClick={submit}
        disabled={!!missing || !lead || textSending}
        title={missing}
        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-civix-purple hover:bg-civix-purpleDeep disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition"
      >
        {textSending ? <Loader2 className="w-4 h-4 animate-spin" />
          : schedule.on ? <CalendarPlus className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
        {schedule.on ? 'Schedule Text' : 'Send Now'}
        {schedule.on && <Clock className="w-3.5 h-3.5 opacity-70" />}
      </button>
      {missing && <div className="text-[10px] text-white/40 text-right">{missing}</div>}

      <div className="pt-2 text-xs text-white/30 text-center">
        Text history is in the lead's Inbox tab
      </div>
    </div>
  );
}
